import { useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { listGroupedFiles } from 'services/fileManager/fileManagerServices';

/**
 * useFetchFiles - React Query hook for fetching case files
 *
 * @param {Object} options - Query options
 * @param {string} options.caseId - Case ID to fetch files for
 * @param {boolean} options.allFiles - Whether to fetch all files or just grouped
 * @param {boolean} options.enabled - Whether the query is enabled
 * @returns {Object} - { files, isLoading: filesLoading, refetch: getFiles, error }
 */
export default function useFetchFiles(options = {}) {
  const { caseId, allFiles = false, enabled = false } = options;
  const [manualParams, setManualParams] = useState(null);
  const queryClient = useQueryClient();

  // Use either passed params or manual params for backward compatibility
  const queryParams = manualParams || { case_id: caseId, all_files: allFiles };

  const query = useQuery({
    queryKey: ['case-files', queryParams.case_id, queryParams.all_files],
    queryFn: async () => {
      const response = await listGroupedFiles(queryParams, true);
      return response.data || [];
    },
    enabled: enabled || !!manualParams?.case_id,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
    refetchOnWindowFocus: false,
  });

  // Backward compatibility: imperative getFiles function
  const getFiles = useCallback((obj) => {
    setManualParams(obj);
  }, []);

  // Backward compatibility: handleSetFiles (deprecated, use React Query's cache directly)
  const handleSetFiles = useCallback(
    (updateFn) => {
      queryClient.setQueryData(['case-files', queryParams.case_id, queryParams.all_files], (oldData) => {
        if (!oldData) return oldData;
        return typeof updateFn === 'function' ? updateFn(oldData) : updateFn;
      });
    },
    [queryClient, queryParams.case_id, queryParams.all_files]
  );

  return {
    files: query.data || [],
    filesLoading: query.isLoading || query.isFetching,
    getFiles,
    handleSetFiles,
    error: query.error,
    refetch: query.refetch,
    isError: query.isError,
  };
}
